import 'core-js/stable';
import 'regenerator-runtime/runtime';

import { selectAuth } from '@brands/store/selectors/auth';
import { AuthState } from '@brands/store/slices/authSlice';
import { SnackbarProvider } from 'notistack';
import React, { FC, useEffect } from 'react';

import { Card } from './assets/types';
import IdleTimer from './brands/default/Components/IdleTimer';
import Toast from './brands/default/Components/ToastContainer/ToastContainer';
import { config } from './brands/default/config/config';
import VideoModal from './brands/default/Dashboard/ConsultationPage/Modals/VideoModal/VideoModal';
import { useAppSelector } from './brands/default/hooks/useReduxHook';
import useScript from './brands/default/hooks/useScript';
import { UserRoleName } from './brands/default/services/identity/types/UserProfile';
import Routing from './Routes/Routes';

declare global {
  interface Window {
    dataLayer: any[];
    Square: {
      payments: (appId: string, locationId: string) => { card: () => Promise<Card> };
    };
  }
}

const App: FC = () => {
  const userData: AuthState = useAppSelector(selectAuth);
  useScript(config.square.src);

  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };

    setVh();
    window.addEventListener('resize', setVh);

    return () => {
      window.removeEventListener('resize', setVh);
    };
  }, []);

  useEffect(() => {
    const analytics = (window as any).cioanalytics || [];

    if (!analytics.initialize && !analytics.invoked) {
      analytics.invoked = true;
      analytics.methods = [
        'trackSubmit',
        'trackClick',
        'trackLink',
        'trackForm',
        'pageview',
        'identify',
        'reset',
        'group',
        'track',
        'ready',
        'alias',
        'debug',
        'page',
        'once',
        'off',
        'on',
        'addSourceMiddleware',
        'addIntegrationMiddleware',
        'setAnonymousId',
        'addDestinationMiddleware',
      ];

      analytics.factory = function (method: string) {
        return function (...args: any[]) {
          args.unshift(method);
          analytics.push(args);
          return analytics;
        };
      };

      for (const method of analytics.methods) {
        analytics[method] = analytics.factory(method);
      }

      analytics.load = function (key: string, options?: any) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.setAttribute('data-global-customerio-analytics-key', 'cioanalytics');
        script.src = `https://cdp.customer.io/v1/analytics-js/snippet/${key}/analytics.min.js`;
        document.getElementsByTagName('head')[0].appendChild(script);
        analytics._writeKey = key;
        analytics._loadOptions = options;
      };

      analytics.SNIPPET_VERSION = '4.15.3';

      analytics.load(process.env.REACT_APP_CUSTOMER_IO_API_KEY!);
      analytics.page();

      (window as any).cioanalytics = analytics;
    }
  }, []);

  return (
    <>
      <SnackbarProvider anchorOrigin={{ vertical: 'top', horizontal: 'right' }} maxSnack={3} preventDuplicate>
        <IdleTimer />
        {userData.userInfo?.role.name !== UserRoleName.Patient && <VideoModal />}
        <Routing />
      </SnackbarProvider>
      <Toast />
    </>
  );
};
export default App;
